import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Form, Input, Table } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { formListAddFocus } from "../../../../../utils/form/form-list-add-focus.ts"
import type {
  TemplateDocumentFormData,
  TemplateDocumentWarranty
} from "../../../types/template-document-form-data.tsx"
import { DurationFormItem } from "../DurationFormItem.tsx"

type FieldType = TemplateDocumentWarranty["items"]

interface WarrantyBlockProps {
  form: FormInstance<TemplateDocumentFormData>
  index: number
}

export function WarrantyBlock({ form, index }: WarrantyBlockProps) {
  const key: keyof TemplateDocumentFormData = "warranty"

  const hiddenColumns =
    Form.useWatch(["blocks", index, "config", "hidden_columns"], form) || []

  return (
    <>
      <Form.Item<TemplateDocumentFormData>
        name={[key, "description"]}
        label="Warranty Description"
      >
        <Input.TextArea placeholder="Enter description" rows={2} />
      </Form.Item>

      <Form.List name={[key, "items"]}>
        {(fields, { add, remove }) => (
          <Table
            pagination={false}
            scroll={{ x: "max-content" }}
            dataSource={fields}
            rowKey="key"
            bordered
            columns={[
              {
                hidden: hiddenColumns?.includes("warranty_type"),
                title: "Type",
                dataIndex: "warranty_type",
                key: "warranty_type",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "warranty_type"]}
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, whitespace: true, message: "Required" }
                    ]}
                  >
                    <Input placeholder="Enter type" />
                  </Form.Item>
                )
              },
              {
                hidden: hiddenColumns?.includes("duration"),
                title: "Duration",
                width: 260,
                dataIndex: "duration",
                key: "duration",
                render: (_, field) => (
                  <DurationFormItem<TemplateDocumentFormData>
                    durationName={[field.name, "duration_value"]}
                    typeName={[field.name, "duration_type"]}
                    formItemProps={{ className: "mb-0" }}
                  />
                )
              },
              {
                hidden: hiddenColumns?.includes("covers"),
                title: "Covers",
                dataIndex: "covers",
                // width: 250,
                key: "covers",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "covers"]}
                    style={{ margin: 0 }}
                    rules={[{ required: false, whitespace: true }]}
                  >
                    <Input placeholder="Enter covers" />
                  </Form.Item>
                )
              },
              {
                hidden: hiddenColumns?.includes("excludes"),
                title: "Excludes",
                dataIndex: "excludes",
                key: "excludes",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "excludes"]}
                    style={{ margin: 0 }}
                    rules={[{ required: false, whitespace: true }]}
                  >
                    <Input placeholder="Enter excludes" />
                  </Form.Item>
                )
              },
              {
                title: "",
                width: 50,
                render: (_, field) => (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                )
              }
            ]}
            footer={() => (
              <Button
                type="dashed"
                onClick={() => {
                  formListAddFocus<TemplateDocumentFormData>(
                    add,
                    fields,
                    form,
                    [key, "items"],
                    ["warranty_type"]
                  )
                }}
                block
                icon={<PlusOutlined />}
              >
                Warranty Item
              </Button>
            )}
          />
        )}
      </Form.List>
    </>
  )
}
